/**
 * Terugbetalen per overschrijving.
 *
 * Een pin-betaling is bij Rabo niet vanuit onze app terug te draaien
 * (docs/open-punten.md, punt 8), en contant mag niet (regel 6). Dus: de kassa
 * legt vast aan wie en op welk IBAN, en de admin maakt er één SEPA-bestand
 * van — dezelfde code als de kraam-uitbetalingen. Regel 13: "binnen één
 * werkdag", en dat is precies waar de uitvoerdatum op staat.
 */

import { controleerIban, normaliseerIban } from './iban.js';
import { maakSepaBestand, type SepaBestand } from './sepa.js';
import type { Cent } from './geld.js';

export interface Rekening {
  iban: string;
  naam: string;
}

export type RekeningControle =
  | { ok: true; rekening: Rekening }
  | { ok: false; melding: string };

/** Wat de kassa controleert vóór hij een terugbetaling vastlegt. */
export function controleerRekening(invoer: { iban: string; naam: string }): RekeningControle {
  const naam = invoer.naam.trim();
  if (naam === '') {
    return { ok: false, melding: 'Vul de naam van de rekeninghouder in.' };
  }
  const iban = controleerIban(invoer.iban);
  if (!iban.geldig) {
    return { ok: false, melding: iban.melding ?? 'Dit IBAN klopt niet.' };
  }
  return { ok: true, rekening: { iban: normaliseerIban(invoer.iban), naam } };
}

export interface OpenTerugbetaling {
  id: string;
  naam: string;
  iban: string;
  bedragCent: Cent;
  /** Bijvoorbeeld het bonnummer of de reden — komt op het afschrift van de klant. */
  omschrijving: string;
}

export interface TerugbetalingsbatchInvoer {
  terugbetalingen: readonly OpenTerugbetaling[];
  opdrachtgeverNaam: string;
  opdrachtgeverIban: string;
  /** UTC. */
  nu: Date;
  /** YYYY-MM-DD, de eerstvolgende werkdag. */
  uitvoerdatum: string;
}

export function batchId(nu: Date): string {
  const stempel = nu.toISOString().replace(/[-:]/g, '').slice(0, 13); // YYYYMMDDTHHMM
  return `QC-TB-${stempel}`;
}

/** Eén SEPA-bestand met alle open terugbetalingen. */
export function maakTerugbetalingsbatch(invoer: TerugbetalingsbatchInvoer): SepaBestand {
  return maakSepaBestand({
    berichtId: batchId(invoer.nu),
    aangemaaktOp: invoer.nu,
    uitvoerdatum: invoer.uitvoerdatum,
    opdrachtgeverNaam: invoer.opdrachtgeverNaam,
    opdrachtgeverIban: invoer.opdrachtgeverIban,
    opdrachten: invoer.terugbetalingen.map((terugbetaling) => ({
      kenmerk: terugbetaling.id.slice(0, 8),
      naam: terugbetaling.naam,
      iban: terugbetaling.iban,
      bedragCent: terugbetaling.bedragCent,
      omschrijving: `Terugbetaling Quality Cashless - ${terugbetaling.omschrijving}`,
    })),
  });
}
